import {
  defineLayoutModule,
  type ViewLayoutModuleContext,
  type ViewLayoutModuleMode
} from '@databases/view-layout-modules';
import { z } from 'zod';

/**
 * Timeline's registerable view-layout module: the layout id, a typed settings
 * schema, and the module factory.
 *
 * Timeline settings are stored in the generic per-view `properties` bag (owned
 * by this module's converter), never as columns on `YView`. Core model, collab,
 * and SDK code only ever see Timeline through `@databases/view-layout-modules`.
 * Do not add Timeline-specific fields/getters/setters to shared Databases
 * classes.
 *
 * This module has no SDK dependency (only `@databases/view-layout-modules` +
 * `zod`); read/write Timeline settings through the SDK's generic typed property
 * methods with {@link TIMELINE_LAYOUT_ID}.
 */
export const TIMELINE_LAYOUT_ID = 'timeline' as const;

export const TIMELINE_SCALES = ['days', 'weeks', 'months', 'quarters', 'years'] as const;

/**
 * Scale supported by the durable Timeline layout module.
 */
export type TimelineScale = (typeof TIMELINE_SCALES)[number];

/**
 * Typed schema for Timeline's durable layout settings.
 *
 * Every field declares a `.default(...)` so {@link defineLayoutModule} can
 * derive canonical defaults: persisted views missing a key receive the default
 * on read, so adding a new setting is a one-line schema change with no
 * migration.
 */
export const timelineLayoutSchema = z.object({
  startDateFieldId: z.string().default(''),
  endDateFieldId: z.string().default(''),
  colorFieldId: z.string().default(''),
  showNumberedEntries: z.boolean().default(true),
  timelineFixedFieldId: z.string().default(''),
  timelineScale: z.enum(TIMELINE_SCALES).default('months')
});

/**
 * Runtime shape of Timeline settings (all keys present, defaults applied).
 */
export type TimelineLayoutSettings = z.infer<typeof timelineLayoutSchema>;

export const DEFAULT_TIMELINE_LAYOUT_SETTINGS: TimelineLayoutSettings =
  timelineLayoutSchema.parse({});

/**
 * Input shape for writes (every key optional; defaults fill the rest on read).
 */
export type TimelineLayoutSettingsInput = z.input<typeof timelineLayoutSchema>;

export const isTimelineScale = (value: unknown): value is TimelineScale =>
  TIMELINE_SCALES.includes(value as TimelineScale);

export type CreateTimelineViewLayoutModuleOptions = {
  /**
   * Feature-gates Timeline writes. Return `read-write` when Timeline is enabled,
   * `read-only` as the rollback mode once Timeline data can exist (blocks new
   * writes while preserving persisted settings), and `off` when unsupported.
   */
  getMode: (ctx: ViewLayoutModuleContext) => ViewLayoutModuleMode;
};

/**
 * Creates the registered Timeline layout module.
 *
 * This is the single feature-gated registration point for Timeline data writes.
 * Register it once at app bootstrap via `createViewLayoutRegistry`; switch
 * `getMode` instead of scattering Timeline feature-flag checks across mutation
 * paths.
 */
export const createTimelineViewLayoutModule = ({
  getMode
}: CreateTimelineViewLayoutModuleOptions) =>
  defineLayoutModule({
    id: TIMELINE_LAYOUT_ID,
    schema: timelineLayoutSchema,
    getMode
  });
